import React, { useState, useContext } from 'react';
import { translate } from 'react-multi-lang';
import { DownloadOutlined, UploadOutlined, CheckCircleTwoTone, CloseCircleTwoTone, WarningTwoTone } from '@ant-design/icons';

import { Row, Col, Button, Upload, Table, Progress, Space, Divider, Alert, message, Tag } from 'antd';
import moment from 'moment';
import 'moment/locale/ca';
import 'moment/locale/es';

import * as XLSX from 'xlsx';

import { AllHtmlEntities } from 'html-entities';
import { Api } from '../../../../helpers';
import { PerfilUsuariContext } from "../../../../context/perfil-usuari";
const entities = new AllHtmlEntities();

// ---------- Constants ----------
const COLUMNES = [
  'id_empresa',
  'id_pressupost',
  'id_alumne',
  'concepte_rebut',
  'import',
  'tipus_pagament',
  'concepte_linia',
  'preu',
  'unitats',
  'subtotal'
];

const TIPUS_PAGAMENT_VALIDS = ['TRANSFERENCIA', 'EFECTIU'];
const ID_EMPRESA_VALIDS = [1, 2];
const TOLERANCIA = 0.01;

// ---------- Helpers ----------
const esEnter = (v) => Number.isInteger(Number(v)) && !isNaN(Number(v));
const esNumeric = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
const aprox = (a, b) => Math.abs(Number(a) - Number(b)) <= TOLERANCIA;

function normalitzarFila(fila){
  // Passa totes les claus a lowercase + trim
  const nova = {};
  Object.keys(fila).forEach((k) => {
    nova[String(k).trim().toLowerCase()] = fila[k];
  });
  return nova;
}

function validarFila(fila){
  const errors = [];
  const avisos = [];

  // Presència
  COLUMNES.forEach((c) => {
    if(fila[c] === undefined || fila[c] === null || fila[c] === ''){
      errors.push(`Falta '${c}'`);
    }
  });
  if(errors.length > 0) return { errors, avisos };

  // Tipus i rangs
  if(!esEnter(fila.id_empresa) || !ID_EMPRESA_VALIDS.includes(Number(fila.id_empresa))){
    errors.push("id_empresa ha de ser 1 o 2");
  }
  if(!esEnter(fila.id_pressupost) || Number(fila.id_pressupost) <= 0){
    errors.push("id_pressupost ha de ser un enter > 0");
  }
  if(!esEnter(fila.id_alumne) || Number(fila.id_alumne) <= 0){
    errors.push("id_alumne ha de ser un enter > 0");
  }
  if(!esNumeric(fila.import) || Number(fila.import) < 0){
    errors.push("import ha de ser numèric >= 0");
  }
  if(!TIPUS_PAGAMENT_VALIDS.includes(String(fila.tipus_pagament).toUpperCase())){
    errors.push(`tipus_pagament ha de ser un de: ${TIPUS_PAGAMENT_VALIDS.join(', ')}`);
  }
  if(!esNumeric(fila.preu) || Number(fila.preu) < 0){
    errors.push("preu ha de ser numèric >= 0");
  }
  if(!esEnter(fila.unitats) || Number(fila.unitats) < 1){
    errors.push("unitats ha de ser un enter >= 1");
  }
  if(!esNumeric(fila.subtotal) || Number(fila.subtotal) < 0){
    errors.push("subtotal ha de ser numèric >= 0");
  }

  // Coherències (avisos)
  if(errors.length === 0){
    const producte = Number(fila.preu) * Number(fila.unitats);
    if(!aprox(producte, Number(fila.subtotal))){
      avisos.push(`preu × unitats (${producte.toFixed(2)}) ≠ subtotal (${Number(fila.subtotal).toFixed(2)})`);
    }
    if(!aprox(Number(fila.subtotal), Number(fila.import))){
      avisos.push(`subtotal (${Number(fila.subtotal).toFixed(2)}) ≠ import (${Number(fila.import).toFixed(2)})`);
    }
  }

  return { errors, avisos };
}

// ---------- Component ----------
function RebutsImportar(props){

  const [perfilUsuari] = useContext(PerfilUsuariContext);
  const cursEscolarTriat = perfilUsuari.curs_escolar_actual.id;

  const [files, setFiles] = useState([]);          // files parsejades + validació
  const [nomFitxer, setNomFitxer] = useState(null);
  const [importantEstat, setImportantEstat] = useState('idle'); // idle|processant|acabat
  const [progres, setProgres] = useState(0);
  const [resultats, setResultats] = useState([]);  // per fila: {fila, ok, id_rebut, missatge}

  // ---------- Descarrega de la plantilla ----------
  const descarregarPlantilla = () => {
    const capceleres = COLUMNES;
    const exemple = [
      1,                      // id_empresa
      15,                     // id_pressupost
      100,                    // id_alumne
      'Quota Anual curs 2026-27',  // concepte_rebut
      681.50,                  // import
      'TRANSFERENCIA',        // tipus_pagament
      'Quota educativa',      // concepte_linia
      681.50,                  // preu
      1,                      // unitats
      681.50                   // subtotal
    ];
    const wsRebuts = XLSX.utils.aoa_to_sheet([capceleres, exemple]);

    const instruccions = [
      ['Instruccions per omplir la plantilla'],
      [''],
      ['1. Cada fila del full "Rebuts" representa un rebut sencer (capçalera + una única línia).'],
      ['2. No canviïs els noms de les columnes.'],
      ['3. Columnes obligatòries: totes.'],
      [''],
      ['Detall de columnes:'],
      ['id_empresa       -> 1 = School, 2 = Uniforms'],
      ['id_pressupost    -> enter > 0'],
      ['id_alumne        -> ID de l\'alumne al sistema'],
      ['concepte_rebut   -> text visible al rebut'],
      ['import           -> import total del rebut (>= 0)'],
      ['tipus_pagament   -> TRANSFERENCIA / EFECTIU'],
      ['concepte_linia   -> concepte de l\'única línia'],
      ['preu             -> preu unitari (>= 0)'],
      ['unitats          -> enter >= 1'],
      ['subtotal         -> total de la línia. Ideal: preu * unitats i coincident amb import'],
    ];
    const wsInstr = XLSX.utils.aoa_to_sheet(instruccions);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsRebuts, 'Rebuts');
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instruccions');
    XLSX.writeFile(wb, 'plantilla_rebuts.xlsx');
  };

  // ---------- Lectura del fitxer ----------
  const llegirFitxer = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try{
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheetName = wb.SheetNames.includes('Rebuts') ? 'Rebuts' : wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const filesRaw = XLSX.utils.sheet_to_json(sheet, { defval: null });

        const validades = filesRaw.map((fila, idx) => {
          const norm = normalitzarFila(fila);
          const { errors, avisos } = validarFila(norm);
          return {
            key: idx,
            fila: idx + 2, // +2 perquè fila 1 = capçalera i idx és 0-based
            dades: norm,
            errors,
            avisos
          };
        });

        setFiles(validades);
        setNomFitxer(file.name);
        setResultats([]);
        setImportantEstat('idle');
        setProgres(0);

        if(validades.length === 0){
          message.warning('El fitxer no conté cap fila de dades.');
        }
      }catch(err){
        console.error('Error llegint Excel:', err);
        message.error('No s\'ha pogut llegir el fitxer Excel.');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // evitar upload automàtic d'antd
  };

  // ---------- Importació ----------
  const importar = async () => {
    const filesValides = files.filter(f => f.errors.length === 0);
    if(filesValides.length === 0){
      message.warning('No hi ha cap fila vàlida per importar.');
      return;
    }

    setImportantEstat('processant');
    setProgres(0);
    const resultatsAcumulats = [];

    for(let i = 0; i < filesValides.length; i++){
      const item = filesValides[i];
      const d = item.dades;

      const dades_rebut = {
        id_curs_escolar: cursEscolarTriat,
        id_empresa: Number(d.id_empresa),
        id_pressupost: Number(d.id_pressupost),
        id_alumne: Number(d.id_alumne),
        concepte: entities.encode(String(d.concepte_rebut)),
        import: Number(d.import),
        id_banc: 0,
        bic: null,
        iban: null,
        titular: null,
        mandat: null,
        mandat_data: null,
        tipus_pagament: String(d.tipus_pagament).toUpperCase(),
        estat: 'pendent',
        fcrea: moment.utc(new Date()).toISOString()
      };

      let resFila = { key: item.key, fila: item.fila, ok: false, id_rebut: null, missatge: '' };

      try{
        const resposta = await Api.myFetch('/rebuts', 'POST', JSON.stringify(dades_rebut));
        if(resposta && resposta.result && resposta.result.success === 1){
          const id_rebut = resposta.data.id;
          resFila.id_rebut = id_rebut;

          const dades_linia = {
            tipus: 'quota_educativa',
            taula: '',
            id_taula: 0,
            concepte: entities.encode(String(d.concepte_linia)),
            descompte: 0,
            participacio: 100,
            preu: Number(d.preu),
            unitats: Number(d.unitats),
            subtotal: Number(d.subtotal),
            manual: 2
          };

          try{
            const resposta2 = await Api.myFetch('/rebuts/linia/' + id_rebut, 'POST', JSON.stringify(dades_linia));
            if(resposta2 && resposta2.result && resposta2.result.success === 1){
              resFila.ok = true;
              resFila.missatge = `Rebut #${id_rebut} creat correctament`;
            }else{
              resFila.ok = false;
              resFila.missatge = `Rebut #${id_rebut} creat però ha fallat la línia: ${(resposta2 && resposta2.data && resposta2.data.message) || 'error desconegut'}`;
            }
          }catch(errLinia){
            resFila.ok = false;
            resFila.missatge = `Rebut #${id_rebut} creat però ha fallat la línia (excepció): ${errLinia.message || errLinia}`;
          }
        }else{
          resFila.ok = false;
          resFila.missatge = `Error creant capçalera: ${(resposta && resposta.data && resposta.data.message) || 'error desconegut'}`;
        }
      }catch(err){
        resFila.ok = false;
        resFila.missatge = `Excepció creant capçalera: ${err.message || err}`;
      }

      resultatsAcumulats.push(resFila);
      setResultats([...resultatsAcumulats]);
      setProgres(Math.round(((i + 1) / filesValides.length) * 100));
    }

    setImportantEstat('acabat');
    const ok = resultatsAcumulats.filter(r => r.ok).length;
    const ko = resultatsAcumulats.length - ok;
    if(ko === 0){
      message.success(`${ok} rebuts importats correctament.`, 6);
    }else{
      message.warning(`Importació finalitzada: ${ok} OK, ${ko} amb error.`, 8);
    }
  };

  // ---------- Columnes de les taules ----------
  const nErrors = files.filter(f => f.errors.length > 0).length;
  const nAvisos = files.filter(f => f.errors.length === 0 && f.avisos.length > 0).length;
  const nValides = files.filter(f => f.errors.length === 0).length;

  const colsPreview = [
    { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60 },
    {
      title: 'Estat',
      key: 'estat',
      width: 110,
      render: (_, r) => {
        if(r.errors.length > 0) return <Tag color="red">Error</Tag>;
        if(r.avisos.length > 0) return <Tag color="orange">Avís</Tag>;
        return <Tag color="green">OK</Tag>;
      }
    },
    { title: 'id_empresa',    dataIndex: ['dades','id_empresa'],    key: 'id_empresa',    width: 90 },
    { title: 'id_pressupost', dataIndex: ['dades','id_pressupost'], key: 'id_pressupost', width: 110 },
    { title: 'id_alumne',     dataIndex: ['dades','id_alumne'],     key: 'id_alumne',     width: 90 },
    { title: 'concepte_rebut',dataIndex: ['dades','concepte_rebut'],key: 'concepte_rebut'},
    { title: 'import',        dataIndex: ['dades','import'],        key: 'import',        width: 90 },
    { title: 'tipus_pagament',dataIndex: ['dades','tipus_pagament'],key: 'tipus_pagament',width: 130 },
    { title: 'concepte_linia',dataIndex: ['dades','concepte_linia'],key: 'concepte_linia'},
    { title: 'preu',          dataIndex: ['dades','preu'],          key: 'preu',          width: 80 },
    { title: 'unitats',       dataIndex: ['dades','unitats'],       key: 'unitats',       width: 70 },
    { title: 'subtotal',      dataIndex: ['dades','subtotal'],      key: 'subtotal',      width: 90 },
    {
      title: 'Missatges',
      key: 'msg',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          {r.errors.map((e, i) => <span key={'e'+i} style={{color:'#cf1322'}}>• {e}</span>)}
          {r.avisos.map((a, i) => <span key={'a'+i} style={{color:'#d46b08'}}>• {a}</span>)}
        </Space>
      )
    }
  ];

  const colsResultats = [
    { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60 },
    {
      title: 'Estat',
      key: 'estat',
      width: 100,
      render: (_, r) => r.ok
        ? <CheckCircleTwoTone twoToneColor="#52c41a" />
        : <CloseCircleTwoTone twoToneColor="#cf1322" />
    },
    { title: 'id_rebut', dataIndex: 'id_rebut', key: 'id_rebut', width: 100 },
    { title: 'Missatge', dataIndex: 'missatge', key: 'missatge' }
  ];

  const podeImportar =
    files.length > 0 &&
    nValides > 0 &&
    importantEstat !== 'processant';

  return (
    <div style={{ padding: 20 }}>
      <Row gutter={16} align="middle">
        <Col>
          <Button icon={<DownloadOutlined />} onClick={descarregarPlantilla}>
            {props.t ? props.t('rebuts.import.descarregar_plantilla') || 'Descarregar plantilla' : 'Descarregar plantilla'}
          </Button>
        </Col>
        <Col>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={llegirFitxer}
            showUploadList={false}
            disabled={importantEstat === 'processant'}
          >
            <Button icon={<UploadOutlined />}>
              {props.t ? props.t('rebuts.import.triar_fitxer') || 'Triar fitxer Excel' : 'Triar fitxer Excel'}
            </Button>
          </Upload>
        </Col>
        {nomFitxer && (
          <Col>
            <span style={{ color: '#555' }}>{nomFitxer}</span>
          </Col>
        )}
      </Row>

      {files.length > 0 && (
        <>
          {/*<Divider />*/}
          <Row gutter={16} style={{ marginTop: 10, marginBottom: 0 }}>
            <Col>
              <Alert
                type={nErrors > 0 ? 'error' : (nAvisos > 0 ? 'warning' : 'success')}
                showIcon
                message={
                  <>
                    Total files: <b>{files.length}</b> · Vàlides: <b>{nValides}</b> · Amb avís: <b>{nAvisos}</b> · Amb error: <b>{nErrors}</b>
                  </>
                }
              />
            </Col>
          </Row>

          <Table
            size="small"
            columns={colsPreview}
            dataSource={files}
            pagination={{ pageSize: 15, position: ['topRight', 'bottomRight'] }}
            scroll={{ x: 1400 }}
            rowClassName={(r) => r.errors.length > 0 ? 'row-error' : (r.avisos.length > 0 ? 'row-warn' : '')}
          />

          <Row gutter={16} style={{ marginTop: 16 }} align="middle">
            <Col>
              <Button onClick={props.callback}>
                {props.t ? props.t('generic.back') : 'Enrere'}
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={importar}
                loading={importantEstat === 'processant'}
                disabled={!podeImportar}
              >
                Importar {nValides} rebut(s)
              </Button>
            </Col>
            {importantEstat !== 'idle' && (
              <Col flex="auto">
                <Progress percent={progres} status={importantEstat === 'acabat' ? 'success' : 'active'} />
              </Col>
            )}
          </Row>
        </>
      )}

      {resultats.length > 0 && (
        <>
          <Divider>Resultats de la importació</Divider>
          <Table
            size="small"
            columns={colsResultats}
            dataSource={resultats}
            pagination={{ pageSize: 20, position: ['topRight', 'bottomRight'] }}
          />
        </>
      )}

      {files.length === 0 && (
        <Row style={{ marginTop: 20 }}>
          <Col>
            <Button onClick={props.callback}>
              {props.t ? props.t('generic.back') : 'Enrere'}
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default translate(RebutsImportar);
